// InstanceWebStoreOrders.js
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import PropTypes from "prop-types";
import * as XLSX from "xlsx";
import { useHistory, useLocation } from "react-router-dom";
import { csvEscape, getPromoStoreOrderItems, isLikelyPromoStoreTestOrder, normalizeReportGroupValue } from "../utils/promoStoreOrderItems";
import PromoStoreReportPreviewDialog from "../reports/PromoStoreReportPreviewDialog";
import { AuthContext } from "../../../../../helpers/AuthContext";
import { isAdministratorRole } from "../../../../../utils/instanceRoleInheritance";
import {
  Box,
  Paper,
  Typography,
  Button,
  ButtonGroup,
  Chip,
  Stack,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  Radio,
  FormControlLabel,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  LinearProgress,
} from "@mui/material";

const STATUS_FILTERS = [
  { label: "All", value: "all" },
  { label: "Pending", value: "pending" },
  { label: "Processing", value: "processing" },
  { label: "Completed", value: "completed" },
];


const sanitizePromoStoreFilenamePart = (value = "") =>
  String(value ?? "")
    .trim()
    .replace(/[^a-z0-9]+/gi, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase() || "promostore";

// ---------- Helper mappers for flexible Woo payloads ----------

const getOrderNumber = (order) =>
  order?.order_number ??
  order?.OrderNumber ??
  order?.OrderId ??
  order?.OrderID ??
  order?.id ??
  order?.ID ??
  "—";

const getOrderStatusRaw = (order) =>
  order?.status ?? order?.order_status ?? order?.OrderStatus ?? "";

const getOrderStatus = (order) => {
  const raw = String(getOrderStatusRaw(order) || "").trim();
  if (!raw) return "Unknown";

  const lower = raw.toLowerCase();
  if (lower.startsWith("wc-")) {
    return raw
      .replace(/^wc-/, "")
      .replace(/-/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }

  return raw
    .replace(/_/g, " ")
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const getOrderTotalNumber = (order) => {
  const raw =
    order?.total ??
    order?.Total ??
    order?.order_total ??
    order?.totalAmount ??
    order?.total_amount ??
    order?.totalValue ??
    order?.total_value;

  if (raw == null || raw === "") return 0;

  const num =
    typeof raw === "number"
      ? raw
      : parseFloat(String(raw).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(num) ? num : 0;
};

const getOrderTotalDisplay = (order) => {
  const amount = getOrderTotalNumber(order);
  const currency =
    order?.currency ??
    order?.Currency ??
    order?.currency_code ??
    order?.CurrencyCode ??
    "";

  if (currency) return `${currency.toUpperCase()} ${amount.toFixed(2)}`;
  return `$${amount.toFixed(2)}`;
};

const formatOrderItemMoney = (value, order) => {
  const currency = String(order?.currency || order?.Currency || order?.currency_code || "CAD").toUpperCase();
  const amount = Number(value);
  return `${currency} ${(Number.isFinite(amount) ? amount : 0).toFixed(2)}`;
};

// WooCommerce custom checkout fields are dynamic. Preserve labels from the
// WordPress REST contract and support older orders with visible metadata.
const getPromoStoreCheckoutFields = (order = {}) => {
  const raw = order?.rawPayload || order || {};
  const explicit = raw.elitecore_checkout_fields || order.elitecore_checkout_fields;
  const metadata = Array.isArray(raw.meta_data) ? raw.meta_data : [];
  const entries = Array.isArray(explicit) && explicit.length
    ? explicit
    : metadata.filter((entry) => /^(?:_?asi_custom_checkout_field(?:_\\d+)?|_?order_location|_?division)$/i.test(String(entry?.key || "")));
  const seen = new Set();
  return entries.flatMap((entry) => {
    const key = String(entry?.key || "").trim();
    const label = String(entry?.label || entry?.display_key || (key === 'division' || key === '_division' ? 'Division' : key.includes('order_location') ? 'Order Location' : key.replace(/^_/, '').replace(/_/g, ' '))).trim();
    const value = entry?.value;
    if (!key || !label || value == null || typeof value === "object" || String(value).trim() === "" || seen.has(key)) return [];
    seen.add(key);
    return [{ key, label, value: String(value).trim() }];
  });
};

const getOrderCustomerName = (order) => {
  const direct =
    order?.customer_name ??
    order?.CustomerName ??
    order?.customerName ??
    order?.customer ??
    order?.Customer;

  if (direct && typeof direct === "string") return direct;

  const billing = order?.billing || {};
  const first =
    billing.first_name ??
    billing.firstName ??
    order?.billing_first_name ??
    order?.billingFirstName;
  const last =
    billing.last_name ??
    billing.lastName ??
    order?.billing_last_name ??
    order?.billingLastName;

  const composed = [first, last].filter(Boolean).join(" ");
  if (composed) return composed;

  const email =
    billing.email ??
    order?.billing_email ??
    order?.customer_email ??
    order?.CustomerEmail;

  if (email) return email;

  return "Unknown customer";
};

const getOrderDate = (order) => {
  const raw =
    order?.date_created ??
    order?.createdAt ??
    order?.created_at ??
    order?.order_date ??
    order?.OrderDate ??
    order?.dateCreated;

  if (!raw) return "—";

  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return String(raw);

  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getOrderAdminUrl = (order) =>
  order?.admin_url ||
  order?.adminUrl ||
  order?.wc_admin_url ||
  order?.woocommerce_admin_url ||
  order?.view_url ||
  order?.viewUrl ||
  order?.edit_url ||
  order?.editUrl ||
  null;

function getOrderIdKey(order) {
  const key =
    order?.id ??
    order?.ID ??
    order?.orderId ??
    order?.OrderId ??
    order?.order_id ??
    order?.wooOrderId;

  return String(key ?? getOrderNumber(order));
}

const isLikelyTestOrder = isLikelyPromoStoreTestOrder;

const getShippingLabelPayload = (order) => {
  const raw = order?.rawPayload || {};
  const shipping = order?.shippingAddress || raw?.shipping || {};
  const billing = order?.billingAddress || raw?.billing || {};

  const first = shipping?.first_name || billing?.first_name || '';
  const last = shipping?.last_name || billing?.last_name || '';
  const fullName = [first, last].filter(Boolean).join(' ').trim();

  const company = shipping?.company || billing?.company || '';
  const address1 = shipping?.address_1 || shipping?.address1 || billing?.address_1 || billing?.address1 || '';
  const address2 = shipping?.address_2 || shipping?.address2 || billing?.address_2 || billing?.address2 || '';
  const city = shipping?.city || billing?.city || '';
  const state = shipping?.state || billing?.state || '';
  const postcode = shipping?.postcode || shipping?.zip || billing?.postcode || billing?.zip || '';
  const country = shipping?.country || billing?.country || '';

  const cityStatePostal = [city, state, postcode].filter(Boolean).join(', ').replace(', ,', ',');

  return {
    orderNumber: getOrderNumber(order),
    recipientName: fullName || getOrderCustomerName(order) || '—',
    company,
    lines: [address1, address2, cityStatePostal, country].filter(Boolean),
  };
};

const escapeHtml = (value = '') =>
  String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');

const PROVIDER_OVERRIDE_OPTIONS = [
  { value: 'default', label: 'Use default provider' },
  { value: 'shipstation', label: 'Force ShipStation' },
  { value: 'core', label: 'Force Core' },
];

const getProviderDisplayName = (provider = 'core') =>
  String(provider || 'core').toLowerCase() === 'shipstation' ? 'ShipStation' : 'Core';

const getJobRowStatusInfo = (row = {}) => {
  const status = String(row?.status || '').toLowerCase();
  if (status === 'completed') return { label: 'labeled', color: 'success' };
  if (status === 'failed_preflight') return { label: 'missing data', color: 'warning' };
  if (status.startsWith('failed')) return { label: 'failed', color: 'error' };
  if (status === 'processing') return { label: 'processing', color: 'info' };
  if (status.startsWith('skipped')) return { label: 'labeled', color: 'success' };
  return { label: 'ready', color: 'default' };
};

const getOrderItems = getPromoStoreOrderItems;

const formatGroupedPromoStoreOrderItemRows = (order = {}) => {
  const items = getOrderItems(order);

  if (!items.length) {
    return [{
      rowType: 'item',
      productName: 'No line items found',
      sku: '',
      quantity: '',
      unitPrice: '',
      lineTotal: '',
      variation: '',
      decoration: '',
    }];
  }

  const grouped = new Map();
  items.forEach((item) => {
    const key = JSON.stringify({
      productName: normalizeReportGroupValue(item.productName),
      sku: normalizeReportGroupValue(item.sku),
      variation: normalizeReportGroupValue(item.variation),
      decoration: normalizeReportGroupValue(item.decoration),
    });

    if (!grouped.has(key)) {
      grouped.set(key, {
        rowType: 'item',
        productName: item.productName,
        sku: item.sku,
        quantity: Number(item.quantity) || 0,
        unitPrice: Number(item.unitPrice) || 0,
        lineTotal: Number(item.lineTotal) || 0,
        variation: item.variation,
        decoration: item.decoration,
      });
      return;
    }

    const existing = grouped.get(key);
    existing.quantity = (Number(existing.quantity) || 0) + (Number(item.quantity) || 0);
    existing.lineTotal = (Number(existing.lineTotal) || 0) + (Number(item.lineTotal) || 0);
    if ((Number(existing.unitPrice) || 0) !== (Number(item.unitPrice) || 0)) {
      existing.unitPrice = existing.quantity ? existing.lineTotal / existing.quantity : existing.unitPrice;
    }
  });

  return Array.from(grouped.values()).map((row) => ({
    ...row,
    quantity: row.quantity || '',
    unitPrice: row.unitPrice ? Number(row.unitPrice).toFixed(2) : '',
    lineTotal: row.lineTotal ? Number(row.lineTotal).toFixed(2) : '',
  }));
};

const buildPromoStoreOrderReportRows = (orders = []) => {
  const rows = [];
  const orderList = Array.isArray(orders) ? orders : [];

  orderList.forEach((order) => {
    rows.push({
      rowType: "order",
      orderNumber: getOrderNumber(order),
      customerName: getOrderCustomerName(order),
      orderStatus: getOrderStatus(order),
      orderDate: getOrderDate(order),
      orderTotal: getOrderTotalNumber(order).toFixed(2),
      productName: "",
      sku: "",
      quantity: "",
      unitPrice: "",
      lineTotal: "",
      variation: "",
      decoration: "",
    });

    formatGroupedPromoStoreOrderItemRows(order).forEach((itemRow) => {
      rows.push({
        rowType: itemRow.rowType,
        orderNumber: "",
        customerName: "",
        orderStatus: "",
        orderDate: "",
        orderTotal: "",
        productName: itemRow.productName,
        sku: itemRow.sku,
        quantity: itemRow.quantity,
        unitPrice: itemRow.unitPrice,
        lineTotal: itemRow.lineTotal,
        variation: itemRow.variation,
        decoration: itemRow.decoration,
      });
    });
  });

  return rows;
};

const ORDER_REPORT_PREVIEW_CONFIRM_THRESHOLD = 1000;

const REPORT_COLUMNS = [
  "rowType",
  "orderNumber",
  "customerName",
  "orderStatus",
  "orderDate",
  "orderTotal",
  "productName",
  "sku",
  "quantity",
  "unitPrice",
  "lineTotal",
  "variation",
  "decoration",
];

const LABEL_FORMAT_OPTIONS = {
  thermal_4_25x2: {
    label: 'Thermal 4.25" × 2"',
    pageWidthIn: 4.25,
    pageHeightIn: 2,
    labelWidthIn: 4.25,
    labelHeightIn: 2,
    marginIn: 0.06,
    paddingIn: 0,
    cols: 1,
    rows: 1,
    gapXIn: 0,
    gapYIn: 0,
  },
  thermal_4x2: {
    label: 'Thermal 4" × 2"',
    pageWidthIn: 4,
    pageHeightIn: 2,
    labelWidthIn: 4,
    labelHeightIn: 2,
    marginIn: 0.06,
    paddingIn: 0,
    cols: 1,
    rows: 1,
    gapXIn: 0,
    gapYIn: 0,
  },
  thermal_4x6: {
    label: 'Thermal 4" × 6"',
    pageWidthIn: 4,
    pageHeightIn: 6,
    labelWidthIn: 4,
    labelHeightIn: 6,
    marginIn: 0.08,
    paddingIn: 0.1,
    cols: 1,
    rows: 1,
    gapXIn: 0,
    gapYIn: 0,
  },
  sheet_2x5: {
    label: 'Sheet 2 × 5 Grid (10/page)',
    pageWidthIn: 8.5,
    pageHeightIn: 11,
    labelWidthIn: 4,
    labelHeightIn: 2,
    marginIn: 0.12,
    paddingIn: 0,
    cols: 2,
    rows: 5,
    gapXIn: 0.2,
    gapYIn: 0.1,
  },
  avery_45005: {
    label: 'Avery 45005 (10/page)',
    pageWidthIn: 8.5,
    pageHeightIn: 11,
    labelWidthIn: 4,
    labelHeightIn: 2,
    marginIn: 0.12,
    paddingIn: 0,
    cols: 2,
    rows: 5,
    gapXIn: 0.2,
    gapYIn: 0.1,
  },
};

const buildCustomLabelFormat = (widthIn = 4.25, heightIn = 2) => ({
  label: `Custom ${widthIn}" × ${heightIn}"`,
  pageWidthIn: widthIn,
  pageHeightIn: heightIn,
  labelWidthIn: widthIn,
  labelHeightIn: heightIn,
  marginIn: 0.06,
  paddingIn: 0,
  cols: 1,
  rows: 1,
  gapXIn: 0,
  gapYIn: 0,
});

const DEFAULT_LABEL_SETTINGS = {
  defaultFormat: 'thermal_4_25x2',
  labelsPerPreviewPage: 30,
  largeRunConfirmationThreshold: 300,
  customLabelWidthIn: 4.25,
  customLabelHeightIn: 2,
  labelLogoUrl: null,
};

// ---------- Main component ----------

const parseCsvText = (text = "") => {
  const csv = String(text || "");
  if (!csv.trim()) return [];

  const rows = [];
  let currentRow = [];
  let currentCell = "";
  let inQuotes = false;

  for (let i = 0; i < csv.length; i += 1) {
    const char = csv[i];
    const nextChar = csv[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        currentCell += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (!inQuotes && char === ",") {
      currentRow.push(currentCell);
      currentCell = "";
      continue;
    }

    if (!inQuotes && (char === "\n" || char === "\r")) {
      if (char === "\r" && nextChar === "\n") i += 1;
      currentRow.push(currentCell);

      if (currentRow.some((cell) => String(cell || "").trim() !== "")) {
        rows.push(currentRow);
      }

      currentRow = [];
      currentCell = "";
      continue;
    }

    currentCell += char;
  }

  if (currentCell.length > 0 || currentRow.length > 0) {
    currentRow.push(currentCell);
    if (currentRow.some((cell) => String(cell || "").trim() !== "")) {
      rows.push(currentRow);
    }
  }

  if (rows.length < 2) return [];

  const headers = rows[0].map((header) => String(header || "").trim());
  return rows.slice(1).map((values) => {
    const obj = {};
    headers.forEach((header, index) => {
      obj[header] = String(values[index] || "").trim();
    });
    return obj;
  });
};


const buildConversionInputSignature = ({ mode = "orders", orderIds = [], csvInput = "" }) => {
  const normalizedOrderIds = (Array.isArray(orderIds) ? orderIds : [])
    .map((id) => String(id))
    .sort();

  return JSON.stringify({
    mode,
    orderIds: normalizedOrderIds,
    csvInput: mode === "csv" ? String(csvInput || "").trim() : "",
  });
};

const formatCurrencyAmount = (amount, currencyCode = "") => {
  const numeric = Number(amount) || 0;
  const normalizedCurrency = String(currencyCode || "").trim().toUpperCase();
  if (normalizedCurrency) return `${normalizedCurrency} ${numeric.toFixed(2)}`;
  return `$${numeric.toFixed(2)}`;
};

const InstanceWebStoreOrders = ({
  orders = [],
  storeType = "company-store",
  storeStatus = "live",
  instanceUniqueId = "",
  storeId,
  storeName = "PromoStore",
  isActive = false,
}) => {
  const history = useHistory();
  const location = useLocation();
  const { authState } = useContext(AuthContext);
  const canViewRawOrderData = isAdministratorRole(authState?.role);
  const [localOrders, setLocalOrders] = useState(Array.isArray(orders) ? orders : []);
  const [ordersRefreshing, setOrdersRefreshing] = useState(false);
  const [ordersRefreshError, setOrdersRefreshError] = useState("");
  const autoOrdersRefreshKeyRef = useRef("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedIds, setSelectedIds] = useState({}); // { [id]: true }
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [convertMode, setConvertMode] = useState("orders");
  const [csvText, setCsvText] = useState("");
  const [csvFileMeta, setCsvFileMeta] = useState(null);
  const [previewState, setPreviewState] = useState({ loading: false, data: null, error: "" });
  const [previewInputSignature, setPreviewInputSignature] = useState("");
  const [selectedPreviewRows, setSelectedPreviewRows] = useState({});
  const [projectName, setProjectName] = useState("PromoStore Converted Project");
  const [creatingProject, setCreatingProject] = useState(false);
  const [viewOrderDialog, setViewOrderDialog] = useState({ open: false, order: null });
  const [labelPreviewOpen, setLabelPreviewOpen] = useState(false);
  const [labelPreviewPage, setLabelPreviewPage] = useState(1);
  const [labelRangeStart, setLabelRangeStart] = useState(1);
  const [labelRangeEnd, setLabelRangeEnd] = useState(1);
  const [defaultLabelCopies, setDefaultLabelCopies] = useState(1);
  const [labelCopiesByOrderId, setLabelCopiesByOrderId] = useState({});
  const [allLabelsSelectedByDefault, setAllLabelsSelectedByDefault] = useState(true);
  const [labelSelectionOverrides, setLabelSelectionOverrides] = useState({});
  const [labelSearchText, setLabelSearchText] = useState("");
  const [labelControlsPage, setLabelControlsPage] = useState(1);
  const [labelControlsPerPage, setLabelControlsPerPage] = useState(50);
  const [orderReportPreviewOpen, setOrderReportPreviewOpen] = useState(false);
  const [orderReportRows, setOrderReportRows] = useState([]);
  const [orderReportConfirmOpen, setOrderReportConfirmOpen] = useState(false);
  const [pendingOrderReportRows, setPendingOrderReportRows] = useState([]);
  const [downloadingOrderReportPdf, setDownloadingOrderReportPdf] = useState(false);
  const [labelSettings, setLabelSettings] = useState(DEFAULT_LABEL_SETTINGS);
  const [selectedLabelFormat, setSelectedLabelFormat] = useState(DEFAULT_LABEL_SETTINGS.defaultFormat);
  const [ordersPage, setOrdersPage] = useState(1);
  const [ordersPerPage, setOrdersPerPage] = useState(100);
  const [instanceDisplayName, setInstanceDisplayName] = useState("ELITE PROMOTIONAL MARKETING");
  const [shippingProviderInfo, setShippingProviderInfo] = useState({
    provider: "core",
    source: "instance-default",
    warnings: [],
  });
  const [providerOverrideMenuAnchor, setProviderOverrideMenuAnchor] = useState(null);
  const [selectedProviderOverride, setSelectedProviderOverride] = useState("default");
  const [labelJobState, setLabelJobState] = useState({ loading: false, job: null, error: "" });


  useEffect(() => {
    setLocalOrders(Array.isArray(orders) ? orders : []);
  }, [orders]);

  const refreshOrders = useCallback(async () => {
    if (!instanceUniqueId || !storeId) return;

    setOrdersRefreshing(true);
    setOrdersRefreshError("");
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/promostores/${instanceUniqueId}/${storeId}/orders/refresh`
      );
      setLocalOrders(Array.isArray(response.data?.orders) ? response.data.orders : []);
    } catch (error) {
      console.error("Failed to refresh PromoStore orders:", error);
      setOrdersRefreshError(
        error?.response?.data?.message ||
          "Failed to refresh orders from WordPress. Cached orders remain visible."
      );
    } finally {
      setOrdersRefreshing(false);
    }
  }, [instanceUniqueId, storeId]);

  useEffect(() => {
    if (!isActive || !instanceUniqueId || !storeId) return;

    const refreshKey = `${instanceUniqueId}:${storeId}`;
    if (autoOrdersRefreshKeyRef.current === refreshKey) return;

    autoOrdersRefreshKeyRef.current = refreshKey;
    refreshOrders();
  }, [instanceUniqueId, isActive, refreshOrders, storeId]);

  const normalizedOrders = useMemo(
    () => (Array.isArray(localOrders) ? localOrders : []),
    [localOrders]
  );

  useEffect(() => {
    const loadLabelSettings = async () => {
      if (!instanceUniqueId) return;
      try {
        const headers = { headers: { accessToken: localStorage.getItem('accessToken') } };
        const instanceRes = await axios.get(
          `${process.env.REACT_APP_NODEJS_SERVER_URL}/instances/byinstanceUniqueId/${instanceUniqueId}`,
          headers
        );
        const instanceId = instanceRes.data?.id;
        const resolvedInstanceName = String(
          instanceRes.data?.instanceName || instanceRes.data?.name || instanceUniqueId || "ELITE PROMOTIONAL MARKETING"
        ).trim();
        if (resolvedInstanceName) setInstanceDisplayName(resolvedInstanceName);
        if (!instanceId) return;
        const settingsRes = await axios.get(
          `${process.env.REACT_APP_NODEJS_SERVER_URL}/instances/${instanceId}/promostore-settings`,
          headers
        );
        const incoming = settingsRes.data?.settings?.shippingLabelSettings || {};
        const integration = settingsRes.data?.settings?.shippingIntegrationSettings || {};
        const merged = { ...DEFAULT_LABEL_SETTINGS, ...incoming };
        setLabelSettings(merged);
        setSelectedLabelFormat(merged.defaultFormat || DEFAULT_LABEL_SETTINGS.defaultFormat);
        setShippingProviderInfo((prev) => ({
          ...prev,
          provider: integration.shippingProviderDefault || "core",
        }));
      } catch (error) {
        console.error('Failed to load PromoStore shipping label settings:', error);
      }
    };

    loadLabelSettings();
  }, [instanceUniqueId]);

  useEffect(() => {
    if (!convertDialogOpen) return;
    setPreviewState({ loading: false, data: null, error: "" });
    setPreviewInputSignature("");
    setSelectedPreviewRows({});
  }, [convertMode]);

  useEffect(() => {
    resolveEffectiveShippingProvider().catch((error) => {
      console.error('Initial provider resolve failed:', error);
    });
  }, [instanceUniqueId]);

  const nonTestOrders = useMemo(
    () => normalizedOrders.filter((order) => !isLikelyTestOrder(order)),
    [normalizedOrders]
  );
  const hiddenTestOrdersCount = Math.max(0, normalizedOrders.length - nonTestOrders.length);

  const filteredOrders = useMemo(() => {
    if (statusFilter === "all") return nonTestOrders;
    
    return nonTestOrders.filter((order) => {
      const raw = String(getOrderStatusRaw(order) || "").toLowerCase();
    
      if (statusFilter === "pending") {
        return raw.includes("pending") || raw.includes("quote-requested");
      }
    
      return raw.includes(statusFilter);
    });
    
  }, [nonTestOrders, statusFilter]);

  useEffect(() => {
    setOrdersPage(1);
  }, [statusFilter, filteredOrders.length]);

  const eligibleOrders = useMemo(() => {
    // Only pending/processing are eligible in this phase
    return nonTestOrders.filter((order) => {
      const s = String(getOrderStatusRaw(order) || "").toLowerCase();
      return s.includes("pending") || s.includes("processing") || s.includes("quote-requested");
    });    
  }, [nonTestOrders]);

  const estimatedTotal = useMemo(
    () => filteredOrders.reduce((sum, order) => sum + getOrderTotalNumber(order), 0),
    [filteredOrders]
  );

  const totalOrdersPages = Math.max(1, Math.ceil(filteredOrders.length / ordersPerPage));
  const pagedOrders = useMemo(() => {
    const start = (ordersPage - 1) * ordersPerPage;
    return filteredOrders.slice(start, start + ordersPerPage);
  }, [filteredOrders, ordersPage, ordersPerPage]);

  useEffect(() => {
    setOrdersPage((current) => Math.min(current, totalOrdersPages));
  }, [totalOrdersPages]);

  const handleChangeFilter = (_event, value) => {
    if (!value) return;
    setStatusFilter(value);
  };

  const handleOpenInWoo = (order) => {
    const url = getOrderAdminUrl(order);
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const printShippingLabels = (labelData = [], formatConfig = LABEL_FORMAT_OPTIONS.thermal_4_25x2) => {
    const list = Array.isArray(labelData) ? labelData : [];
    if (!list.length) {
      alert("No orders available for label printing.");
      return;
    }

    const threshold = Number(labelSettings.largeRunConfirmationThreshold) || DEFAULT_LABEL_SETTINGS.largeRunConfirmationThreshold;
    if (list.length > threshold) {
      const proceed = window.confirm(
        `You are about to generate ${list.length} labels. This may take a while in your browser. Continue?`
      );
      if (!proceed) return;
    }

    const format = formatConfig || LABEL_FORMAT_OPTIONS.thermal_4_25x2;
    const logoUrl = labelSettings?.labelLogoUrl || "";
    const brandName = String(instanceDisplayName || "ELITE PROMOTIONAL MARKETING").trim();
    const labelsPerSheet = Math.max(1, Number(format.cols || 1) * Number(format.rows || 1));
    const safeMarginIn = Math.max(0, Number(format.marginIn) || 0);
    const safePaddingIn = Math.max(0, Number(format.paddingIn) || 0);
    const pageInsetIn = labelsPerSheet > 1 ? safeMarginIn : safeMarginIn + safePaddingIn;

    const renderCard = (label) => {
      const lines = [label.recipientName, label.company, ...(label.lines || [])].filter(Boolean);
      return `
        <div class="label-card">
          <div class="label-header">
            <div class="brand-wrap">
              ${logoUrl
                ? `<img class="label-logo" src="${escapeHtml(logoUrl)}" alt="Label logo" />`
                : `<div class="brand-text">${escapeHtml(brandName)}</div>`}
            </div>
            <div class="return-address">14703 - 118 Avenue<br/>Edmonton, Alberta T5L 2M7<br/>Tel: 780.454.9775</div>
          </div>
          <div class="label-body">
            ${lines.map((line) => `<div class="line">${escapeHtml(line)}</div>`).join('')}
          </div>
          <div class="label-footer">
            <span>Order #:</span> <strong>${escapeHtml(label.orderNumber)}</strong>
            <span class="box">Box ____ of ____</span>
          </div>
        </div>
      `;
    };

    let html = '';
    if (labelsPerSheet === 1) {
      html = list
        .map((label) => `<section class="label-page">${renderCard(label)}</section>`)
        .join('');
    } else {
      for (let i = 0; i < list.length; i += labelsPerSheet) {
        const pageLabels = list.slice(i, i + labelsPerSheet);
        const cells = pageLabels.map((label) => `<div class="grid-cell">${renderCard(label)}</div>`).join('');
        html += `<section class="label-page sheet-grid">${cells}</section>`;
      }
    }

    const popup = window.open('', '_blank');
    if (!popup) {
      alert('Unable to open the print preview window because your browser blocked the popup. Please allow popups for this site and try again. This is not caused by missing printer hardware. You can still review labels in the on-screen preview dialog.');
      return;
    }

    popup.document.write(`
      <html>
      <head>
        <title>PromoStore Shipping Labels</title>
        <style>
          @page { size: ${format.pageWidthIn}in ${format.pageHeightIn}in; margin: 0; }
          * { box-sizing: border-box; }
          html, body { margin: 0; padding: 0; font-family: Arial, sans-serif; }
          .label-page { width: ${format.pageWidthIn}in; height: ${format.pageHeightIn}in; page-break-after: always; break-after: page; page-break-inside: avoid; break-inside: avoid-page; overflow: hidden; padding: ${pageInsetIn}in; }
          .sheet-grid { display: grid; grid-template-columns: repeat(${format.cols || 1}, ${format.labelWidthIn}in); grid-template-rows: repeat(${format.rows || 1}, ${format.labelHeightIn}in); gap: ${format.gapYIn || 0}in ${format.gapXIn || 0}in; align-content: start; justify-content: start; }
          .grid-cell { width: ${format.labelWidthIn}in; height: ${format.labelHeightIn}in; }
          .label-card { width: 100%; height: 100%; border: 2px solid #2d3d7a; border-radius: 10px; display: flex; flex-direction: column; overflow: hidden; page-break-inside: avoid; break-inside: avoid-page; }
          .label-header { border-bottom: 1px solid #2d3d7a; padding: 0.08in 0.12in 0.06in; position: relative; min-height: 0.6in; }
          .brand-wrap { display: flex; align-items: center; gap: 0.08in; }
          .label-logo { width: 1.2in; max-height: 0.46in; object-fit: contain; }
          .brand-text { font-size: 0.16in; font-weight: 700; color: #2d3d7a; line-height: 1.1; max-width: 1.55in; word-break: break-word; }
          .return-address { position: absolute; right: 0.12in; top: 0.08in; font-size: 0.12in; line-height: 1.15; text-align: left; color: #222; }
          .label-body { flex: 1; min-height: 0; border-bottom: 1px solid #2d3d7a; padding: 0.08in 0.12in; overflow: hidden; }
          .line { font-size: 0.15in; line-height: 1.2; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
          .label-footer { padding: 0.05in 0.12in; font-size: 0.14in; display: flex; align-items: center; justify-content: space-between; }
          .box { margin-left: 0.2in; }
          @media print { .label-page:last-child { page-break-after: auto; } }
        </style>
      </head>
      <body>${html}</body>
      </html>
    `);
    popup.document.close();
    popup.focus();

    const tryPrintPopup = () => {
      try {
        popup.print();
      } catch (_error) {
        alert('Print preview opened, but the browser could not start printing automatically. Please use the print command in the opened tab (Ctrl/Cmd+P).');
      }
    };

    popup.onload = tryPrintPopup;
    setTimeout(tryPrintPopup, 250);
  };

  const resolveEffectiveShippingProvider = async (providerOverride = null) => {
    if (!instanceUniqueId) return { provider: "core", source: "fallback-core", warnings: ["Missing instanceUniqueId"] };
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/shipping/provider/resolve`,
        {
          params: { instanceUniqueId, providerOverride: providerOverride || undefined },
          headers: { accessToken: localStorage.getItem("accessToken") },
        }
      );
      const providerPayload = {
        provider: response.data?.provider || "core",
        source: response.data?.source || "instance-default",
        warnings: Array.isArray(response.data?.warnings) ? response.data.warnings : [],
      };
      setShippingProviderInfo(providerPayload);
      return providerPayload;
    } catch (error) {
      console.error("Failed to resolve shipping provider:", error);
      return { provider: "core", source: "fallback-core", warnings: ["Resolve failed; defaulting to Core."] };
    }
  };

  const loadLabelJob = useCallback(async (jobId, includeRows = true) => {
    if (!jobId) return null;
    const response = await axios.get(
      `${process.env.REACT_APP_NODEJS_SERVER_URL}/shipping/promostore/jobs/${jobId}`,
      {
        params: { includeRows: includeRows ? 1 : 0 },
        headers: { accessToken: localStorage.getItem("accessToken") },
      }
    );
    const job = response.data?.job || null;
    setLabelJobState((prev) => ({ ...prev, job }));
    return job;
  }, []);

  const handleExecuteShippingLabels = async (labels, formatConfig) => {
    const providerOverride = selectedProviderOverride === 'default' ? null : selectedProviderOverride;
    const resolved = await resolveEffectiveShippingProvider(providerOverride);
    if ((resolved.warnings || []).length) {
      console.warn("Shipping provider warnings:", resolved.warnings);
    }

    const orderIds = Array.from(
      new Set((labels || []).map((label) => Number(label.__orderId)).filter(Number.isFinite))
    );

    if (!orderIds.length) {
      alert('No orders selected for label creation.');
      return;
    }

    const promoStoreId = Number(filteredOrders?.[0]?.PromoStoreId || filteredOrders?.[0]?.promoStoreId || 0) || null;

    setLabelJobState({ loading: true, job: labelJobState.job, error: "" });

    try {
      const createRes = await axios.post(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/shipping/promostore/jobs`,
        {
          instanceUniqueId,
          promoStoreId,
          orderIds,
          providerOverride,
          options: { format: formatConfig?.key || selectedLabelFormat },
        },
        { headers: { accessToken: localStorage.getItem("accessToken") } }
      );

      const createdJobId = createRes.data?.jobId;
      if (!createdJobId) throw new Error('Label job id missing from response');
      const job = await loadLabelJob(createdJobId, true);
      setLabelJobState({ loading: false, job, error: "" });
      setLabelPreviewOpen(false);
    } catch (error) {
      console.error('Failed to start mass label job:', error);
      setLabelJobState({ loading: false, job: labelJobState.job, error: error?.response?.data?.message || error?.message || 'Failed to start label job' });
      alert(error?.response?.data?.message || 'Failed to create mass label job');
    }
  };

  const retryFailedLabelRows = async () => {
    const jobId = labelJobState?.job?.id;
    if (!jobId) return;
    try {
      await axios.post(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/shipping/promostore/jobs/${jobId}/retry-failed`,
        {},
        { headers: { accessToken: localStorage.getItem("accessToken") } }
      );
      setLabelJobState((prev) => ({ ...prev, loading: true, error: "" }));
      await loadLabelJob(jobId, true);
    } catch (error) {
      console.error('Failed to retry failed label rows:', error);
      setLabelJobState((prev) => ({ ...prev, loading: false, error: 'Failed to retry failed rows' }));
    }
  };

  const downloadLabelUrlsCsv = () => {
    const rows = Array.isArray(labelJobState?.job?.rows) ? labelJobState.job.rows : [];
    const completed = rows.filter((row) => row?.labelUrl);
    if (!completed.length) {
      alert('No label URLs are available yet.');
      return;
    }

    const lines = ["orderId,trackingNumber,labelUrl,status"];
    completed.forEach((row) => {
      lines.push([
        csvEscape(row?.promoStoreOrderId || ''),
        csvEscape(row?.trackingNumber || ''),
        csvEscape(row?.labelUrl || ''),
        csvEscape(row?.status || ''),
      ].join(','));
    });

    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `promostore-label-urls-${labelJobState.job.id}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  };

  const exportLabelFailuresCsv = () => {
    const rows = Array.isArray(labelJobState?.job?.rows) ? labelJobState.job.rows : [];
    const failed = rows.filter((row) => String(row?.status || '').startsWith('failed'));
    if (!failed.length) {
      alert('No failed label rows found for export.');
      return;
    }

    const lines = ["orderId,status,errorMessage,preflightErrors"];
    failed.forEach((row) => {
      lines.push([
        csvEscape(row?.promoStoreOrderId || ''),
        csvEscape(row?.status || ''),
        csvEscape(row?.errorMessage || ''),
        csvEscape(Array.isArray(row?.preflightErrors) ? row.preflightErrors.join('|') : ''),
      ].join(','));
    });

    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `promostore-label-failures-${labelJobState.job.id}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  };
  const openOrderReportPreview = (ordersForReport = []) => {
    const rows = buildPromoStoreOrderReportRows(ordersForReport);
    if (!rows.length) {
      alert("No orders available for report preview.");
      return;
    }

    if (rows.length > ORDER_REPORT_PREVIEW_CONFIRM_THRESHOLD) {
      setPendingOrderReportRows(rows);
      setOrderReportConfirmOpen(true);
      return;
    }

    setOrderReportRows(rows);
    setOrderReportPreviewOpen(true);
  };

  const downloadOrderReportCsv = (ordersForReport = [], preparedRows = null) => {
    const reportRows = Array.isArray(preparedRows) ? preparedRows : buildPromoStoreOrderReportRows(ordersForReport);
    if (!reportRows.length) {
      alert("No orders available for report export.");
      return;
    }

    const lines = [REPORT_COLUMNS.join(",")];
    reportRows.forEach((row) => {
      lines.push(REPORT_COLUMNS.map((column) => csvEscape(row?.[column] ?? "")).join(","));
    });

    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 10);
    anchor.href = url;
    anchor.download = `promostore-order-report-${stamp}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  };

  const downloadOrderReportXlsx = (ordersForReport = [], preparedRows = null) => {
    const reportRows = Array.isArray(preparedRows) ? preparedRows : buildPromoStoreOrderReportRows(ordersForReport);
    if (!reportRows.length) {
      alert("No orders available for report export.");
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(reportRows, { header: REPORT_COLUMNS });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "PromoStore Orders");
    const stamp = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `promostore-order-report-${stamp}.xlsx`);
  };

  const downloadOrderReportPdf = async (ordersForReport = [], preparedRows = null) => {
    const reportRows = Array.isArray(preparedRows) ? preparedRows : buildPromoStoreOrderReportRows(ordersForReport);
    if (!reportRows.length) {
      alert("No orders available for report export.");
      return;
    }
    if (!storeId) {
      alert("PromoStore details are still loading. Please try again.");
      return;
    }

    setDownloadingOrderReportPdf(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/promostores/${storeId}/orders-report/pdf`,
        {
          storeName,
          instanceUniqueId,
          orders: ordersForReport,
        },
        {
          responseType: "blob",
          headers: { accessToken: localStorage.getItem("accessToken") },
        }
      );
      const disposition = response.headers?.["content-disposition"] || "";
      const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
      const stamp = new Date().toISOString().slice(0, 10);
      const fallbackFilename = `${sanitizePromoStoreFilenamePart(storeName)}-orders-report-${stamp}.pdf`;
      const filename = filenameMatch?.[1] || fallbackFilename;
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download PromoStore orders PDF:", error);
      alert(error?.response?.data?.message || "Failed to download PDF report.");
    } finally {
      setDownloadingOrderReportPdf(false);
    }
  };


  const openOrderDetails = (order) => {
    setViewOrderDialog({ open: true, order: order || null });
  };

  const closeOrderDetails = () => {
    setViewOrderDialog({ open: false, order: null });
  };

  const hasOrders = nonTestOrders.length > 0;


  useEffect(() => {
    if (!isActive) return;
    const query = new URLSearchParams(location.search || "");
    if (query.get("previewOrdersReport") !== "1") return;
    openOrderReportPreview(filteredOrders);
    query.delete("previewOrdersReport");
    history.replace({
      pathname: location.pathname,
      search: query.toString() ? `?${query.toString()}` : "",
    });
  }, [filteredOrders, history, isActive, location.pathname, location.search]);

  useEffect(() => {
    if (!labelPreviewOpen) return;
    setLabelRangeStart(1);
    setLabelRangeEnd(Math.max(1, filteredOrders.length));
    setDefaultLabelCopies(1);
    setLabelCopiesByOrderId({});
    setAllLabelsSelectedByDefault(true);
    setLabelSelectionOverrides({});
    setLabelSearchText("");
    setLabelControlsPage(1);
  }, [labelPreviewOpen, filteredOrders.length]);

  const labelRangeBounds = useMemo(() => {
    const max = Math.max(1, filteredOrders.length);
    const start = Math.min(max, Math.max(1, Number(labelRangeStart) || 1));
    const end = Math.min(max, Math.max(start, Number(labelRangeEnd) || max));
    return { start, end, max };
  }, [filteredOrders.length, labelRangeEnd, labelRangeStart]);

  const rangedOrdersForLabels = useMemo(() => {
    if (!filteredOrders.length) return [];
    return filteredOrders.slice(labelRangeBounds.start - 1, labelRangeBounds.end);
  }, [filteredOrders, labelRangeBounds.end, labelRangeBounds.start]);

  const labelOrderRows = useMemo(
    () => rangedOrdersForLabels.map((order) => ({
      order,
      orderId: getOrderIdKey(order),
      payload: getShippingLabelPayload(order),
    })),
    [rangedOrdersForLabels]
  );

  const isLabelOrderSelected = useCallback(
    (orderId) => {
      const hasOverride = Object.prototype.hasOwnProperty.call(labelSelectionOverrides, orderId);
      if (hasOverride) return Boolean(labelSelectionOverrides[orderId]);
      return allLabelsSelectedByDefault;
    },
    [allLabelsSelectedByDefault, labelSelectionOverrides]
  );

  const normalizedLabelSearch = String(labelSearchText || "").trim().toLowerCase();
  const filteredLabelOrderRows = useMemo(() => {
    if (!normalizedLabelSearch) return labelOrderRows;
    return labelOrderRows.filter(({ payload }) => {
      const haystack = [
        payload?.recipientName,
        payload?.company,
        payload?.orderNumber,
        ...(payload?.lines || []),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(normalizedLabelSearch);
    });
  }, [labelOrderRows, normalizedLabelSearch]);

  useEffect(() => {
    setLabelControlsPage(1);
  }, [normalizedLabelSearch, labelRangeStart, labelRangeEnd]);

  const labelOrderIndexById = useMemo(() => {
    const map = {};
    labelOrderRows.forEach(({ orderId }, index) => {
      map[orderId] = labelRangeBounds.start + index;
    });
    return map;
  }, [labelOrderRows, labelRangeBounds.start]);

  const totalLabelControlsPages = Math.max(
    1,
    Math.ceil(filteredLabelOrderRows.length / labelControlsPerPage)
  );
  const pagedLabelControlRows = useMemo(() => {
    const start = (labelControlsPage - 1) * labelControlsPerPage;
    return filteredLabelOrderRows.slice(start, start + labelControlsPerPage);
  }, [filteredLabelOrderRows, labelControlsPage, labelControlsPerPage]);

  useEffect(() => {
    setLabelControlsPage((page) => Math.min(page, totalLabelControlsPages));
  }, [totalLabelControlsPages]);

  const activePreviewLabels = useMemo(() => {
    const labels = [];
    const fallbackCopies = Math.max(0, Math.round(Number(defaultLabelCopies) || 1));

    filteredLabelOrderRows.forEach(({ orderId, payload }) => {
      if (!isLabelOrderSelected(orderId)) return;
      const overrideCopies = labelCopiesByOrderId[orderId];
      const copies = Math.max(
        0,
        Math.round(Number(overrideCopies ?? fallbackCopies) || 0)
      );
      for (let copyIndex = 0; copyIndex < copies; copyIndex += 1) {
        labels.push({
          ...payload,
          __orderId: orderId,
          __copyIndex: copyIndex + 1,
        });
      }
    });

    return labels;
  }, [defaultLabelCopies, filteredLabelOrderRows, isLabelOrderSelected, labelCopiesByOrderId]);

  const labelsPerPreviewPage = Number(labelSettings.labelsPerPreviewPage) || DEFAULT_LABEL_SETTINGS.labelsPerPreviewPage;
  const previewOrderRows = filteredLabelOrderRows;
  const totalLabelPages = Math.max(1, Math.ceil(previewOrderRows.length / labelsPerPreviewPage));
  const pagedPreviewOrderRows = useMemo(() => {
    const start = (labelPreviewPage - 1) * labelsPerPreviewPage;
    return previewOrderRows.slice(start, start + labelsPerPreviewPage);
  }, [previewOrderRows, labelPreviewPage, labelsPerPreviewPage]);

  useEffect(() => {
    setLabelPreviewPage((current) => Math.min(current, totalLabelPages));
  }, [totalLabelPages]);

  const activeLabelFormat = useMemo(() => {
    if (selectedLabelFormat === 'custom') {
      return buildCustomLabelFormat(
        Number(labelSettings.customLabelWidthIn) || DEFAULT_LABEL_SETTINGS.customLabelWidthIn,
        Number(labelSettings.customLabelHeightIn) || DEFAULT_LABEL_SETTINGS.customLabelHeightIn
      );
    }
    return LABEL_FORMAT_OPTIONS[selectedLabelFormat] || LABEL_FORMAT_OPTIONS[labelSettings.defaultFormat] || LABEL_FORMAT_OPTIONS.thermal_4_25x2;
  }, [selectedLabelFormat, labelSettings]);

  const labelsPerSheetForActiveFormat = Math.max(
    1,
    Number(activeLabelFormat?.cols || 1) * Number(activeLabelFormat?.rows || 1)
  );

  const latestLabelRowsByOrderId = useMemo(() => {
    const rows = Array.isArray(labelJobState?.job?.rows) ? labelJobState.job.rows : [];
    const map = {};
    rows.forEach((row) => {
      map[String(row?.promoStoreOrderId)] = row;
    });
    return map;
  }, [labelJobState?.job?.rows]);

  const labelJobTotals = labelJobState?.job?.totals || {};
  const labelJobProgress = Math.min(
    100,
    Math.round(((Number(labelJobTotals.processed) || 0) / Math.max(1, Number(labelJobTotals.requested) || 0)) * 100)
  );

  useEffect(() => {
    const jobId = labelJobState?.job?.id;
    const status = String(labelJobState?.job?.status || '').toLowerCase();
    if (!jobId) return undefined;
    if (!['queued', 'processing'].includes(status)) return undefined;

    const timer = setInterval(() => {
      loadLabelJob(jobId, true).catch((error) => {
        console.error('Failed polling label job status:', error);
      });
    }, 3000);

    return () => clearInterval(timer);
  }, [labelJobState?.job?.id, labelJobState?.job?.status, loadLabelJob]);

  // Selection helpers (company store)
  const selectedList = useMemo(
    () => Object.keys(selectedIds).filter((k) => selectedIds[k]),
    [selectedIds]
  );

  const allFilteredSelected = useMemo(() => {
    if (!filteredOrders.length) return false;
    return filteredOrders.every((o) => selectedIds[getOrderIdKey(o)]);
  }, [filteredOrders, selectedIds]);

  const toggleSelectAllFiltered = () => {
    setSelectedIds((prev) => {
      const next = { ...prev };
      const shouldSelect = !allFilteredSelected;
      filteredOrders.forEach((o) => {
        next[getOrderIdKey(o)] = shouldSelect;
      });
      return next;
    });
  };

  const toggleSelectOne = (order) => {
    const key = getOrderIdKey(order);
    setSelectedIds((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Convert button logic
  const isPopup = storeType === "pop-up-store";
  const isCompany = storeType === "company-store";
  const isProgram = storeType === "program";
  const currentUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user_data') || '{}');
    } catch (_error) {
      return {};
    }
  }, []);
  const userRoles = useMemo(() => {
    const roles = [];
    if (Array.isArray(currentUser?.roles)) roles.push(...currentUser.roles);
    if (currentUser?.role) roles.push(currentUser.role);
    return roles.map((role) => String(role || '').toLowerCase()).filter(Boolean);
  }, [currentUser]);
  const canOverrideProvider = userRoles.includes('admin') || userRoles.includes('administrator');

  const popupAllowed = isPopup && storeStatus === "closed";
  const popupDisabledReason = !popupAllowed
    ? "Pop-up stores can only convert orders to a project after the store is closed."
    : "";

  const companyDisabledReason =
    selectedList.length === 0 ? "Select one or more orders to convert." : "";

  const showConvertButton = !isProgram; // hide for program for now

  const convertLabel = isPopup
    ? "Convert all pending/processing to project"
    : "Convert selected to project";

  const canConvert =
    (isPopup && popupAllowed && eligibleOrders.length > 0) ||
    (isCompany && selectedList.length > 0);

  const getCurrentOrderIdsForConvert = () => (isPopup ? eligibleOrders.map(getOrderIdKey) : selectedList);

  const handleConvert = async () => {
    const orderIds = getCurrentOrderIdsForConvert();
    if (!orderIds.length) return;
    setConvertMode("orders");
    setProjectName(isPopup ? "Pop-up Store Closeout Project" : "Company Store Batch Project");
    setPreviewState({ loading: false, data: null, error: "" });
    setPreviewInputSignature("");
    setSelectedPreviewRows({});
    setCsvText("");
    setCsvFileMeta(null);
    setConvertDialogOpen(true);
  };

  const handleCsvFilePicked = (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader?.result || "");
      setCsvText(text);
      setCsvFileMeta({ name: file.name, size: file.size });
      setPreviewState({ loading: false, data: null, error: "" });
      setPreviewInputSignature("");
      setSelectedPreviewRows({});
    };
    reader.onerror = () => {
      setPreviewState({ loading: false, data: null, error: "Failed to read CSV file." });
    };
    reader.readAsText(file);

    if (event?.target) {
      event.target.value = "";
    }
  };

  const clearCsvInput = () => {
    setCsvText("");
    setCsvFileMeta(null);
    setPreviewState({ loading: false, data: null, error: "" });
    setPreviewInputSignature("");
    setSelectedPreviewRows({});
  };

  const generatePreview = async () => {
    const orderIds = getCurrentOrderIdsForConvert();
    const csvRows = convertMode === "csv" ? parseCsvText(csvText) : [];
    const inputSignature = buildConversionInputSignature({ mode: convertMode, orderIds, csvInput: csvText });
    setPreviewState({ loading: true, data: null, error: "" });
    try {
      const { data } = await axios.post(`${process.env.REACT_APP_NODEJS_SERVER_URL}/promostores/convert/preview`, {
        mode: convertMode,
        orderIds,
        csvRows,
      }, {
        headers: { accessToken: localStorage.getItem("accessToken") },
      });
      const preview = data?.preview || null;
      const defaults = {};
      (preview?.rows || []).forEach((row) => { defaults[row.sourceRowKey] = true; });
      setSelectedPreviewRows(defaults);
      setPreviewInputSignature(inputSignature);
      setPreviewState({ loading: false, data: preview, error: "" });
    } catch (error) {
      setPreviewInputSignature("");
      setPreviewState({ loading: false, data: null, error: error?.response?.data?.message || "Failed to generate preview." });
    }
  };

  const createProjectFromPreview = async () => {
    if (!previewState?.data?.rows?.length) return;
    const orderIds = getCurrentOrderIdsForConvert();
    const currentInputSignature = buildConversionInputSignature({ mode: convertMode, orderIds, csvInput: csvText });

    if (!previewInputSignature || previewInputSignature !== currentInputSignature) {
      alert("Your conversion inputs changed after preview. Please regenerate preview before creating the project.");
      return;
    }
    const csvRows = convertMode === "csv" ? parseCsvText(csvText) : [];
    const selectedRowKeys = Object.keys(selectedPreviewRows).filter((k) => selectedPreviewRows[k]);
    setCreatingProject(true);
    try {
      const { data } = await axios.post(`${process.env.REACT_APP_NODEJS_SERVER_URL}/promostores/convert/create`, {
        mode: convertMode,
        orderIds,
        csvRows,
        selectedRowKeys,
        projectName,
        eventType: storeType === "program" ? "Program" : "Online store",
      }, {
        headers: { accessToken: localStorage.getItem("accessToken") },
      });
      const createdId = data?.orderId || data?.order?.OrderId || data?.order?.id;
      const cartItemsAdded = Number(data?.cartItemsAdded);
      const baseMessage = data?.message || (
        Number.isFinite(cartItemsAdded)
          ? `Project created successfully. ${cartItemsAdded} order item(s) were added to the project cart.`
          : "Project created successfully."
      );
      alert(`${baseMessage}${createdId ? ` (#${createdId})` : ""}.`);
      setConvertDialogOpen(false);
      setPreviewState({ loading: false, data: null, error: "" });
      setSelectedIds({});
    } catch (error) {
      alert(error?.response?.data?.message || "Failed to create project.");
    } finally {
      setCreatingProject(false);
    }
  };


  const currentInputSignature = buildConversionInputSignature({
    mode: convertMode,
    orderIds: getCurrentOrderIdsForConvert(),
    csvInput: csvText,
  });
  const previewOutOfDate = Boolean(previewState.data) && currentInputSignature !== previewInputSignature;
  const previewRows = Array.isArray(previewState.data?.rows) ? previewState.data.rows : [];
  const selectedRows = previewRows.filter((row) => selectedPreviewRows[row.sourceRowKey]);
  const selectedSummary = selectedRows.reduce((acc, row) => {
    const qty = Number(row?.quantity) || 0;
    const amt = Number(row?.lineTotal) || 0;
    if (!row?.appliedDecorationSummary && !row?.decorationSummary) acc.withoutDecoration += 1;
    acc.rowCount += 1;
    acc.quantity += qty;
    acc.amount += amt;
    return acc;
  }, { rowCount: 0, quantity: 0, amount: 0, withoutDecoration: 0 });
  const previewWarnings = Array.isArray(previewState.data?.warnings) ? previewState.data.warnings : [];
  const previewCurrency = previewState.data?.summary?.currency || "";
  const smartNote = previewState.data
    ? [
      `Mode: ${convertMode === "csv" ? "CSV upload" : "Woo orders"}.`,
      `Selected ${selectedSummary.rowCount} line(s) from ${previewRows.length} preview line(s) across ${previewState.data.summary?.orderCount || 0} order(s).`,
      `Estimated selected quantity: ${selectedSummary.quantity}.`,
      `Estimated selected total: ${formatCurrencyAmount(selectedSummary.amount, previewCurrency)}.`,
      selectedSummary.withoutDecoration > 0 ? `${selectedSummary.withoutDecoration} selected line(s) have no decoration summary.` : "All selected lines include decoration summary.",
      previewWarnings.length ? `Warnings detected: ${previewWarnings.length}. Review below before converting.` : "No conversion warnings were reported in preview.",
    ].join(" ")
    : "";

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 2.5 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: 1,
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            PromoStore Orders
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
            Orders synced from WooCommerce for this PromoStore.
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          <Button
            variant="outlined"
            size="small"
            onClick={refreshOrders}
            disabled={ordersRefreshing || !storeId}
            sx={{ textTransform: "none", borderRadius: 999 }}
          >
            {ordersRefreshing ? "Refreshing…" : "Refresh orders"}
          </Button>

          {/* Status Filters */}
          <ToggleButtonGroup
            value={statusFilter}
            exclusive
            onChange={handleChangeFilter}
            size="small"
            sx={{
              backgroundColor: "#fafafa",
              borderRadius: 999,
              p: 0.5,
              border: "1px solid #e0e0e0",
            }}
          >
            {STATUS_FILTERS.map((f) => (
              <ToggleButton
                key={f.value}
                value={f.value}
                sx={{
                  textTransform: "none",
                  px: 2,
                  borderRadius: 999,
                  "&.Mui-selected": {
                    backgroundColor: "#c3171d",
                    color: "#fff",
                    "&:hover": { backgroundColor: "#9f1217" },
                  },
                }}
              >
                {f.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      </Box>

      {ordersRefreshing && <LinearProgress sx={{ borderRadius: 999 }} />}

      {ordersRefreshError && (
        <Alert severity="warning">
          {ordersRefreshError}
        </Alert>
      )}

      {/* Summary Bar */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          border: "1px solid #e0e0e0",
          p: 2,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
          alignItems: { xs: "flex-start", md: "center" },
          justifyContent: "space-between",
        }}
      >
        <Stack direction="row" spacing={3} flexWrap="wrap">
          <Box>
            <Typography variant="body2" sx={{ color: "text.secondary", textTransform: "uppercase" }}>
              Total orders
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5 }}>
              {nonTestOrders.length}
            </Typography>
            {hiddenTestOrdersCount > 0 && (
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {hiddenTestOrdersCount} test submission{hiddenTestOrdersCount === 1 ? "" : "s"} hidden
              </Typography>
            )}
          </Box>

          <Box>
            <Typography variant="body2" sx={{ color: "text.secondary", textTransform: "uppercase" }}>
              In this view
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5 }}>
              {filteredOrders.length}{" "}
              <Typography component="span" variant="body2" sx={{ color: "text.secondary" }}>
                orders
              </Typography>
            </Typography>
          </Box>

          <Box>
            <Typography variant="body2" sx={{ color: "text.secondary", textTransform: "uppercase" }}>
              Estimated value (this view)
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5 }}>
              ${estimatedTotal.toFixed(2)}
            </Typography>
          </Box>
        </Stack>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <Chip
            size="small"
            color={String(shippingProviderInfo.provider || 'core').toLowerCase() === 'shipstation' ? 'info' : 'default'}
            label={`Provider: ${getProviderDisplayName(shippingProviderInfo.provider)}`}
            sx={{ alignSelf: 'center', fontWeight: 600 }}
          />
          <ButtonGroup variant="outlined" size="small" sx={{ borderRadius: 999 }}>
            <Tooltip arrow title='Generate provider-aware shipping labels for all orders in this filtered view.'>
              <span>
                <Button
                  disabled={!filteredOrders.length}
                  onClick={() => setLabelPreviewOpen(true)}
                  sx={{ textTransform: "none", fontWeight: 700, borderRadius: 999 }}
                >
                  Create Shipping Labels ({filteredOrders.length})
                </Button>
              </span>
            </Tooltip>
            {canOverrideProvider && (
              <Button
                size="small"
                onClick={(event) => setProviderOverrideMenuAnchor(event.currentTarget)}
                sx={{ minWidth: 42, fontWeight: 700 }}
              >
                ▾
              </Button>
            )}
          </ButtonGroup>
          <Menu
            anchorEl={providerOverrideMenuAnchor}
            open={Boolean(providerOverrideMenuAnchor)}
            onClose={() => setProviderOverrideMenuAnchor(null)}
          >
            {PROVIDER_OVERRIDE_OPTIONS.map((option) => (
              <MenuItem
                key={option.value}
                selected={selectedProviderOverride === option.value}
                onClick={() => {
                  setSelectedProviderOverride(option.value);
                  setProviderOverrideMenuAnchor(null);
                }}
              >
                {option.label}
              </MenuItem>
            ))}
          </Menu>
          {showConvertButton && (
            <Tooltip
              arrow
              title={
                isPopup
                  ? popupDisabledReason || "Convert all eligible orders into ONE project."
                  : companyDisabledReason || "Convert selected orders into projects (phase 1)."
              }
            >
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={!canConvert}
                  onClick={handleConvert}
                  sx={{ textTransform: "none", fontWeight: 700, borderRadius: 999 }}
                >
                  {convertLabel}
                </Button>
              </span>
            </Tooltip>
          )}
        </Stack>
      </Paper>

      {labelJobState.error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {labelJobState.error}
        </Alert>
      )}

      {labelJobState.job && (
        <Paper
          elevation={0}
          sx={{
            mt: 1,
            borderRadius: 2,
            border: "1px solid #e0e0e0",
            p: 2,
            backgroundColor: "#fcfcfc",
          }}
        >
          <Stack spacing={1.5}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Mass Label Job #{labelJobState.job.id} • {getProviderDisplayName(labelJobState.job.provider)}
              </Typography>
              <Chip
                size="small"
                label={String(labelJobState.job.status || 'queued').toUpperCase()}
                color={['completed', 'partial'].includes(String(labelJobState.job.status || '').toLowerCase()) ? 'success' : String(labelJobState.job.status || '').toLowerCase().startsWith('fail') ? 'error' : 'info'}
              />
            </Stack>

            <LinearProgress variant="determinate" value={labelJobProgress} />
            <Typography variant="caption" color="text.secondary">
              Processed {Number(labelJobTotals.processed) || 0} of {Number(labelJobTotals.requested) || 0} • Success {Number(labelJobTotals.success) || 0} • Failed {Number(labelJobTotals.failed) || 0} • Skipped {Number(labelJobTotals.skipped) || 0}
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Button size="small" variant="outlined" onClick={downloadLabelUrlsCsv}>
                Download Labels
              </Button>
              <Button size="small" variant="outlined" onClick={exportLabelFailuresCsv}>
                Export Failures
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={retryFailedLabelRows}
                disabled={(Number(labelJobTotals.failed) || 0) === 0}
              >
                Retry Failed
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}

      {/* Empty State */}
      {!hasOrders && (
        <Paper
          elevation={0}
          sx={{
            mt: 1,
            p: 4,
            borderRadius: 2,
            border: "1px dashed #d0d0d0",
            textAlign: "center",
            background: "radial-gradient(circle at top left, #fff5f5, #ffffff 55%)",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            No orders yet
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: 420, mx: "auto", mb: 2 }}>
            Once customers place orders in WooCommerce and the site syncs back to Elite Core,
            they’ll appear here automatically.
          </Typography>
          <Typography variant="caption" sx={{ color: "text.disabled", display: "block" }}>
            Tip: Run a sync from the Web Store Actions menu after test checkouts.
          </Typography>
        </Paper>
      )}

      {/* Table */}
      {hasOrders && (
        <Paper
          elevation={0}
          sx={{
            mt: 1,
            borderRadius: 2,
            border: "1px solid #e0e0e0",
            overflow: "hidden",
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
            sx={{ px: 2, py: 1.25, borderBottom: "1px solid #eaeaea", backgroundColor: "#fcfcfc" }}
          >
            <Typography variant="caption" color="text.secondary">
              Rendering {pagedOrders.length} of {filteredOrders.length} orders in this view for faster performance.
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <FormControl size="small" sx={{ minWidth: 110 }}>
                <InputLabel id="orders-per-page">Rows</InputLabel>
                <Select
                  labelId="orders-per-page"
                  label="Rows"
                  value={ordersPerPage}
                  onChange={(event) => {
                    setOrdersPerPage(Number(event.target.value) || 100);
                    setOrdersPage(1);
                  }}
                >
                  {[50, 100, 200, 500].map((size) => (
                    <MenuItem key={size} value={size}>{size}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary">
                Page {ordersPage} of {totalOrdersPages}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setOrdersPage((page) => Math.max(1, page - 1))}
                disabled={ordersPage <= 1}
              >
                Prev
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setOrdersPage((page) => Math.min(totalOrdersPages, page + 1))}
                disabled={ordersPage >= totalOrdersPages}
              >
                Next
              </Button>
            </Stack>
          </Stack>

          <Box
            component="table"
            sx={{
              width: "100%",
              borderCollapse: "collapse",
              "& thead": { backgroundColor: "#fafafa" },
            }}
          >
            <Box component="thead">
              <Box component="tr">
                {/* Selection column (company only) */}
                <Box
                  component="th"
                  sx={{
                    width: 54,
                    textAlign: "center",
                    px: 1,
                    py: 1.5,
                    fontSize: 12,
                    fontWeight: 600,
                    color: "text.secondary",
                    textTransform: "uppercase",
                    borderBottom: "1px solid #e0e0e0",
                  }}
                >
                  {isCompany ? (
                    <Checkbox
                      size="small"
                      checked={allFilteredSelected}
                      onChange={toggleSelectAllFiltered}
                    />
                  ) : null}
                </Box>

                <Box component="th" sx={thSx}>Order #</Box>
                <Box component="th" sx={thSx}>Customer</Box>
                <Box component="th" sx={{ ...thSx, textAlign: "right" }}>Total</Box>
                <Box component="th" sx={thSx}>Status</Box>
                <Box component="th" sx={thSx}>Shipping Label</Box>
                <Box component="th" sx={thSx}>Created</Box>
                <Box component="th" sx={{ ...thSx, textAlign: "right" }}>Actions</Box>
              </Box>
            </Box>

            <Box component="tbody">
              {pagedOrders.map((order, idx) => {
                const orderNumber = getOrderNumber(order);
                const statusLabel = getOrderStatus(order);
                const totalDisplay = getOrderTotalDisplay(order);
                const dateDisplay = getOrderDate(order);
                const adminUrl = getOrderAdminUrl(order);

                const statusRaw = String(getOrderStatusRaw(order) || "").toLowerCase();
                let chipColor = "default";
                if (statusRaw.includes("pending")) chipColor = "warning";
                if (statusRaw.includes("processing")) chipColor = "info";
                if (statusRaw.includes("completed")) chipColor = "success";
                if (statusRaw.includes("cancel") || statusRaw.includes("refunded")) chipColor = "error";

                const rowKey = getOrderIdKey(order);
                const labelRow = latestLabelRowsByOrderId[String(order?.id)] || latestLabelRowsByOrderId[String(rowKey)] || null;
                const labelStatus = getJobRowStatusInfo(labelRow);

                return (
                  <Box
                    component="tr"
                      key={rowKey || idx}
                    sx={{
                      "&:nth-of-type(even)": { backgroundColor: "#fcfcfc" },
                      "&:hover": { backgroundColor: "#f5f5f5" },
                    }}
                  >
                    {/* Selection cell */}
                    <Box
                      component="td"
                      sx={{
                        px: 1,
                        py: 1.25,
                        borderBottom: "1px solid #f0f0f0",
                        textAlign: "center",
                        width: 54,
                      }}
                    >
                      {isCompany ? (
                        <Checkbox
                          size="small"
                          checked={!!selectedIds[rowKey]}
                          onChange={() => toggleSelectOne(order)}
                        />
                      ) : null}
                    </Box>

                    <Box component="td" sx={tdStrongSx}>{orderNumber}</Box>
                    <Box component="td" sx={tdEllipsisSx}>{getOrderCustomerName(order)}</Box>
                    <Box component="td" sx={{ ...tdSx, textAlign: "right" }}>{totalDisplay}</Box>

                    <Box component="td" sx={tdSx}>
                      <Chip
                        label={statusLabel}
                        size="small"
                        color={chipColor}
                        variant={chipColor === "default" ? "outlined" : "filled"}
                        sx={{ fontWeight: 500, textTransform: "capitalize" }}
                      />
                    </Box>

                    <Box component="td" sx={tdSx}>
                      <Chip
                        label={labelStatus.label}
                        size="small"
                        color={labelStatus.color}
                        variant={labelStatus.color === "default" ? "outlined" : "filled"}
                        sx={{ fontWeight: 500, textTransform: "capitalize" }}
                      />
                    </Box>

                    <Box component="td" sx={{ ...tdSx, fontSize: 13, color: "text.secondary" }}>
                      {dateDisplay}
                    </Box>

                    <Box component="td" sx={{ ...tdSx, textAlign: "right" }}>
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => openOrderDetails(order)}
                          sx={{ textTransform: "none", fontWeight: 600 }}
                        >
                          View Order
                        </Button>
                        <Tooltip
                          title={
                            adminUrl
                              ? "Open this order in WooCommerce admin"
                              : "Admin URL not available for this order"
                          }
                        >
                          <span>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleOpenInWoo(order)}
                              disabled={!adminUrl}
                              sx={{
                                textTransform: "none",
                                fontWeight: 600,
                                borderRadius: 999,
                                borderColor: "#c3171d",
                                color: "#c3171d",
                                "&:hover": {
                                  borderColor: "#9f1217",
                                  backgroundColor: "rgba(195,23,29,0.04)",
                                },
                                "&.Mui-disabled": {
                                  borderColor: "#e0e0e0",
                                  color: "#b0b0b0",
                                },
                              }}
                            >
                              View in WooCommerce
                            </Button>
                          </span>
                        </Tooltip>
                      </Stack>
                    </Box>
                  </Box>
                );
              })}

              {filteredOrders.length === 0 && hasOrders && (
                <Box component="tr">
                  <Box
                    component="td"
                    colSpan={8}
                    sx={{
                      px: 2,
                      py: 3,
                      textAlign: "center",
                      color: "text.secondary",
                      fontSize: 14,
                    }}
                  >
                    No orders match this filter yet.
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Paper>
      )}




      <Dialog open={convertDialogOpen} onClose={() => setConvertDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Convert PromoStore to Project</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <RadioGroup row value={convertMode} onChange={(e) => setConvertMode(e.target.value)}>
              <FormControlLabel value="orders" control={<Radio />} label="Use orders" />
              <FormControlLabel value="csv" control={<Radio />} label="Upload CSV" />
            </RadioGroup>

            <TextField size="small" label="Project Name" value={projectName} onChange={(e) => setProjectName(e.target.value)} fullWidth />

            {convertMode === "csv" && (
              <Stack spacing={1.5}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }}>
                  <Button variant="outlined" component="label">
                    Choose CSV File
                    <input hidden type="file" accept=".csv,text/csv" onChange={handleCsvFilePicked} />
                  </Button>
                  <Button variant="text" color="inherit" onClick={clearCsvInput} disabled={!csvText && !csvFileMeta}>Clear CSV</Button>
                  <Typography variant="body2" color="text.secondary">
                    {csvFileMeta ? `${csvFileMeta.name} (${Math.ceil((csvFileMeta.size || 0) / 1024)} KB)` : "No CSV file selected yet."}
                  </Typography>
                </Stack>
                <TextField
                  label="CSV rows (editable)"
                  multiline
                  minRows={5}
                  value={csvText}
                  onChange={(e) => {
                    setCsvText(e.target.value);
                    setCsvFileMeta(null);
                  }}
                  placeholder="orderNumber,name,quantity,unitPrice,lineTotal,decorationSummary"
                  helperText="Upload a CSV file or paste/edit CSV text. Generate Preview is required before Create Project."
                  fullWidth
                />
              </Stack>
            )}

            <Button variant="outlined" onClick={generatePreview} disabled={previewState.loading}>
              {previewState.loading ? "Generating..." : "Generate Preview"}
            </Button>

            {previewState.error && <Alert severity="error">{previewState.error}</Alert>}

            {previewOutOfDate && (
              <Alert severity="warning">
                Inputs changed after preview. Please click Generate Preview again before creating the project.
              </Alert>
            )}

            {previewState.data && (
              <>
                <Alert severity="info">
                  {`Orders: ${previewState.data.summary?.orderCount || 0} · Lines: ${previewState.data.summary?.lineCount || 0} · Qty: ${previewState.data.summary?.totalQuantity || 0} · Total: ${formatCurrencyAmount(previewState.data.summary?.totalAmount || 0, previewCurrency)}`}
                </Alert>

                {previewWarnings.length > 0 && (
                  <Alert severity="warning">
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Conversion Warnings ({previewWarnings.length})</Typography>
                    <Box component="ul" sx={{ m: 0, pl: 2 }}>
                      {previewWarnings.map((warning, idx) => (
                        <li key={`preview-warning-${idx}`}>{warning}</li>
                      ))}
                    </Box>
                  </Alert>
                )}

                <Alert severity="info" sx={{ whiteSpace: "pre-wrap" }}>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Smart Note (not saved)</Typography>
                    <Button
                      size="small"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(smartNote);
                          alert("Smart note copied to clipboard.");
                        } catch (_error) {
                          alert("Unable to copy smart note.");
                        }
                      }}
                    >
                      Copy Note
                    </Button>
                  </Stack>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>{smartNote}</Typography>
                </Alert>

                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">Use</TableCell>
                      <TableCell>Order #</TableCell>
                      <TableCell>Item</TableCell>
                      <TableCell>Qty</TableCell>
                      <TableCell>Decoration</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(previewState.data.rows || []).map((row) => (
                      <TableRow key={row.sourceRowKey}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={Boolean(selectedPreviewRows[row.sourceRowKey])}
                            onChange={(e) => setSelectedPreviewRows((prev) => ({ ...prev, [row.sourceRowKey]: e.target.checked }))}
                          />
                        </TableCell>
                        <TableCell>{row.sourceOrderNumber || "—"}</TableCell>
                        <TableCell>{row.name || "—"}</TableCell>
                        <TableCell>{row.quantity || 0}</TableCell>
                        <TableCell>
                          {row.appliedDecorationSummary || row.decorationSummary || (row.decorationMatchStatus === "needs_review" ? "Decoration needs review" : "—")}
                          {row.decorationMatchStatus === "needs_review" ? (
                            <Typography variant="caption" display="block" color="warning.main">
                              Verify source product and artwork before purchasing
                            </Typography>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConvertDialogOpen(false)}>Close</Button>
          <Button variant="contained" onClick={createProjectFromPreview} disabled={creatingProject || !previewState.data || previewOutOfDate}>
            {creatingProject ? "Creating..." : "Create Project"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={labelPreviewOpen}
        onClose={() => setLabelPreviewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{`Shipping Label Preview (${activePreviewLabels.length} labels across ${previewOrderRows.length} orders)`}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
              <TextField
                size="small"
                type="number"
                label="Order range start"
                inputProps={{ min: 1, max: labelRangeBounds.max }}
                value={labelRangeStart}
                onChange={(event) => setLabelRangeStart(Number(event.target.value) || 1)}
              />
              <TextField
                size="small"
                type="number"
                label="Order range end"
                inputProps={{ min: 1, max: labelRangeBounds.max }}
                value={labelRangeEnd}
                onChange={(event) => setLabelRangeEnd(Number(event.target.value) || labelRangeBounds.max)}
              />
              <TextField
                size="small"
                type="number"
                label="Default copies per order"
                inputProps={{ min: 0, max: 20 }}
                value={defaultLabelCopies}
                onChange={(event) => setDefaultLabelCopies(Number(event.target.value) || 0)}
              />
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  const oneSheetEnd = Math.min(
                    labelRangeBounds.max,
                    labelRangeBounds.start + labelsPerSheetForActiveFormat - 1
                  );
                  setLabelRangeEnd(oneSheetEnd);
                  setDefaultLabelCopies(1);
                  setLabelCopiesByOrderId({});
                  setLabelPreviewPage(1);
                }}
              >
                Test One Sheet
              </Button>
            </Stack>

            <Typography variant="caption" color="text.secondary">
              Labels are generated from orders {labelRangeBounds.start} to {labelRangeBounds.end} in the current filtered view.
              Set default copies for all, then optionally override specific orders below.
            </Typography>

            <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ xs: "stretch", md: "center" }}>
              <TextField
                size="small"
                label="Search labels"
                placeholder="Find recipient, order #, address..."
                value={labelSearchText}
                onChange={(event) => setLabelSearchText(event.target.value)}
                sx={{ width: "100%" }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                {filteredLabelOrderRows.length} matching order{filteredLabelOrderRows.length === 1 ? "" : "s"} in current range.
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setAllLabelsSelectedByDefault(true);
                    setLabelSelectionOverrides({});
                  }}
                >
                  Check All
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setAllLabelsSelectedByDefault(false);
                    setLabelSelectionOverrides({});
                  }}
                >
                  Uncheck All
                </Button>
              </Stack>
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} spacing={1}>
              <Typography variant="body2" color="text.secondary">
                {`Page ${labelPreviewPage} of ${totalLabelPages} (${labelsPerPreviewPage} orders per page)`}
              </Typography>
              <FormControl size="small" sx={{ minWidth: 240 }}>
                <InputLabel id="label-format-select">Label Format</InputLabel>
                <Select
                  labelId="label-format-select"
                  label="Label Format"
                  value={selectedLabelFormat}
                  onChange={(event) => setSelectedLabelFormat(event.target.value)}
                >
                  {Object.entries(LABEL_FORMAT_OPTIONS).map(([value, config]) => (
                    <MenuItem key={value} value={value}>{config.label}</MenuItem>
                  ))}
                  <MenuItem value="custom">Custom ({labelSettings.customLabelWidthIn}" × {labelSettings.customLabelHeightIn}")</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary">
                {`Selected format outputs ${Math.max(1, Number(activeLabelFormat?.cols || 1) * Number(activeLabelFormat?.rows || 1))} label(s) per print page.`}
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setLabelPreviewPage((page) => Math.max(1, page - 1))}
                  disabled={labelPreviewPage <= 1}
                >
                  Previous
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setLabelPreviewPage((page) => Math.min(totalLabelPages, page + 1))}
                  disabled={labelPreviewPage >= totalLabelPages}
                >
                  Next
                </Button>
              </Stack>
            </Stack>

            <Typography variant="caption" color="text.secondary">
              Visual output preview (scaled) for current format: {activeLabelFormat?.label || "Selected format"}
            </Typography>
            <Box
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: 1,
                p: 1,
                background: "#fafafa",
                maxHeight: 300,
                overflowY: "auto",
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 1,
                }}
              >
                {pagedPreviewOrderRows.slice(0, 6).map(({ orderId, payload: label }, idx) => {
                  const previewLines = [label.recipientName, label.company, ...(label.lines || [])].filter(Boolean);
                  const brandText = String(instanceDisplayName || "ELITE PROMOTIONAL MARKETING").trim();
                  return (
                    <Box
                      key={`visual-preview-${orderId}-${idx}`}
                      sx={{
                        border: "2px solid #2d3d7a",
                        borderRadius: 1,
                        p: 1,
                        backgroundColor: "#fff",
                        minHeight: 130,
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                        position: "relative",
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={isLabelOrderSelected(orderId)}
                        onChange={(event) =>
                          setLabelSelectionOverrides((prev) => {
                            const next = { ...prev };
                            const checked = event.target.checked;
                            if (checked === allLabelsSelectedByDefault) {
                              delete next[orderId];
                            } else {
                              next[orderId] = checked;
                            }
                            return next;
                          })
                        }
                        sx={{ position: "absolute", top: 2, right: 2, p: 0.25 }}
                      />
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {labelSettings?.labelLogoUrl ? (
                          <Box
                            component="img"
                            src={labelSettings.labelLogoUrl}
                            alt="Label logo"
                            sx={{ width: 64, maxHeight: 26, objectFit: "contain" }}
                          />
                        ) : (
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 700,
                              color: "#2d3d7a",
                              lineHeight: 1.1,
                              display: "block",
                              maxWidth: 120,
                              wordBreak: "break-word",
                            }}
                          >
                            {brandText}
                          </Typography>
                        )}
                      </Box>
                      <Typography variant="caption" sx={{ fontSize: 10, color: "text.secondary" }}>
                        14703 - 118 Avenue, Edmonton, AB, T5L 2M7 · 780.454.9775
                      </Typography>
                      <Box sx={{ borderTop: "1px solid #d9deef", borderBottom: "1px solid #d9deef", py: 0.5, flex: 1 }}>
                        {previewLines.map((line, lineIdx) => (
                          <Typography key={`line-${idx}-${lineIdx}`} variant="caption" sx={{ display: "block", lineHeight: 1.2 }}>
                            {line}
                          </Typography>
                        ))}
                      </Box>
                      <Typography variant="caption">
                        <strong>Order #:</strong> {label.orderNumber}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>

            <Box
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: 1,
                p: 1,
                maxHeight: 300,
                overflowY: "auto",
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Use</TableCell>
                    <TableCell>#</TableCell>
                    <TableCell>Order #</TableCell>
                    <TableCell>Recipient</TableCell>
                    <TableCell>Address</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedPreviewOrderRows.map(({ orderId, payload: label }, idx) => (
                    <TableRow key={`${orderId}-${idx}`}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={isLabelOrderSelected(orderId)}
                          onChange={(event) =>
                            setLabelSelectionOverrides((prev) => {
                              const next = { ...prev };
                              const checked = event.target.checked;
                              if (checked === allLabelsSelectedByDefault) {
                                delete next[orderId];
                              } else {
                                next[orderId] = checked;
                              }
                              return next;
                            })
                          }
                        />
                      </TableCell>
                      <TableCell>{labelOrderIndexById[orderId] || (labelPreviewPage - 1) * labelsPerPreviewPage + idx + 1}</TableCell>
                      <TableCell>{label.orderNumber}</TableCell>
                      <TableCell>{label.recipientName || "—"}</TableCell>
                      <TableCell>{[label.company, ...(label.lines || [])].filter(Boolean).join(", ")}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>

            <Box
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: 1,
                p: 1,
                maxHeight: 220,
                overflowY: "auto",
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                justifyContent="space-between"
                alignItems={{ xs: "stretch", sm: "center" }}
                sx={{ mb: 1 }}
              >
                <Typography variant="caption" color="text.secondary">
                  Optional copy overrides per order (leave blank to use default copies). Showing {pagedLabelControlRows.length} of {filteredLabelOrderRows.length}.
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <FormControl size="small" sx={{ minWidth: 90 }}>
                    <InputLabel id="label-controls-page-size">Rows</InputLabel>
                    <Select
                      labelId="label-controls-page-size"
                      label="Rows"
                      value={labelControlsPerPage}
                      onChange={(event) => {
                        setLabelControlsPerPage(Number(event.target.value) || 50);
                        setLabelControlsPage(1);
                      }}
                    >
                      {[25, 50, 100, 200].map((size) => (
                        <MenuItem key={size} value={size}>{size}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Typography variant="caption" color="text.secondary">
                    Page {labelControlsPage} of {totalLabelControlsPages}
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setLabelControlsPage((page) => Math.max(1, page - 1))}
                    disabled={labelControlsPage <= 1}
                  >
                    Prev
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setLabelControlsPage((page) => Math.min(totalLabelControlsPages, page + 1))}
                    disabled={labelControlsPage >= totalLabelControlsPages}
                  >
                    Next
                  </Button>
                </Stack>
              </Stack>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Use</TableCell>
                    <TableCell>#</TableCell>
                    <TableCell>Order #</TableCell>
                    <TableCell>Recipient</TableCell>
                    <TableCell sx={{ width: 170 }}>Copies</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedLabelControlRows.map(({ orderId, payload: item }, idx) => {
                    return (
                      <TableRow key={`copies-${orderId}-${idx}`}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={isLabelOrderSelected(orderId)}
                            onChange={(event) =>
                              setLabelSelectionOverrides((prev) => {
                                const next = { ...prev };
                                const checked = event.target.checked;
                                if (checked === allLabelsSelectedByDefault) {
                                  delete next[orderId];
                                } else {
                                  next[orderId] = checked;
                                }
                                return next;
                              })
                            }
                          />
                        </TableCell>
                        <TableCell>{labelOrderIndexById[orderId] || labelRangeBounds.start + idx}</TableCell>
                        <TableCell>{item.orderNumber}</TableCell>
                        <TableCell>{item.recipientName || "—"}</TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={labelCopiesByOrderId[orderId] ?? ""}
                            placeholder={String(defaultLabelCopies)}
                            inputProps={{ min: 0, max: 20 }}
                            onChange={(event) => {
                              const rawValue = event.target.value;
                              setLabelCopiesByOrderId((prev) => {
                                if (rawValue === "") {
                                  const next = { ...prev };
                                  delete next[orderId];
                                  return next;
                                }
                                return { ...prev, [orderId]: Math.max(0, Number(rawValue) || 0) };
                              });

                              if (rawValue !== "" && Number(rawValue) > 0) {
                                setLabelSelectionOverrides((prev) => {
                                  const next = { ...prev };
                                  if (allLabelsSelectedByDefault) {
                                    delete next[orderId];
                                  } else {
                                    next[orderId] = true;
                                  }
                                  return next;
                                });
                              }
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLabelPreviewOpen(false)}>Close</Button>
          <Button
            variant="contained"
            onClick={() => handleExecuteShippingLabels(activePreviewLabels, activeLabelFormat)}
            disabled={!activePreviewLabels.length}
          >
            {`Create ${activePreviewLabels.length} Labels`}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={orderReportConfirmOpen} onClose={() => setOrderReportConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Large report detected</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 1.5 }}>
            This report has {pendingOrderReportRows.length} rows. Opening the full preview can be slow on large datasets.
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Click <strong>Open Full Preview</strong> to continue, or download directly after opening.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOrderReportConfirmOpen(false);
            setPendingOrderReportRows([]);
          }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setOrderReportRows(pendingOrderReportRows);
              setOrderReportPreviewOpen(true);
              setOrderReportConfirmOpen(false);
            }}
          >
            Open Full Preview
          </Button>
        </DialogActions>
      </Dialog>

      <PromoStoreReportPreviewDialog
        open={orderReportPreviewOpen}
        onClose={() => {
          setOrderReportPreviewOpen(false);
          setPendingOrderReportRows([]);
        }}
        title={`PromoStore Orders Report Preview (${orderReportRows.length} rows)`}
        actions={(
          <>
            <Button onClick={() => {
              setOrderReportPreviewOpen(false);
              setPendingOrderReportRows([]);
            }}>Close</Button>
            <Button variant="outlined" onClick={() => downloadOrderReportCsv(filteredOrders, orderReportRows)}>
              Download CSV
            </Button>
            <Button variant="contained" onClick={() => downloadOrderReportXlsx(filteredOrders, orderReportRows)}>
              Download XLSX
            </Button>
            <Button
              variant="contained"
              onClick={() => downloadOrderReportPdf(filteredOrders, orderReportRows)}
              disabled={downloadingOrderReportPdf}
            >
              {downloadingOrderReportPdf ? "Downloading PDF…" : "Download PDF"}
            </Button>
          </>
        )}
      >
        <Stack spacing={1} sx={{ mt: 1, height: "100%" }}>
          <Typography variant="body2" color="text.secondary">
            Preview rows are grouped by order/customer, with item rows listed directly underneath.
          </Typography>
          <Box sx={{ border: "1px solid #e0e0e0", borderRadius: 1, height: "100%", overflow: "auto" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>Order #</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Total</TableCell>
                  <TableCell>Product</TableCell>
                  <TableCell>SKU</TableCell>
                  <TableCell>Qty</TableCell>
                  <TableCell>Unit</TableCell>
                  <TableCell>Line Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orderReportRows.map((row, idx) => (
                  <TableRow
                    key={`report-row-${idx}`}
                    sx={{
                      backgroundColor: row.rowType === "order" ? "#f8f9ff" : "inherit",
                      "& td": row.rowType === "separator" ? { borderBottom: "2px solid #eee" } : {},
                    }}
                  >
                    <TableCell>{row.rowType}</TableCell>
                    <TableCell>{row.orderNumber}</TableCell>
                    <TableCell>{row.customerName}</TableCell>
                    <TableCell>{row.orderStatus}</TableCell>
                    <TableCell>{row.orderDate}</TableCell>
                    <TableCell>{row.orderTotal}</TableCell>
                    <TableCell>{row.productName}</TableCell>
                    <TableCell>{row.sku}</TableCell>
                    <TableCell>{row.quantity}</TableCell>
                    <TableCell>{row.unitPrice}</TableCell>
                    <TableCell>{row.lineTotal}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Stack>
      </PromoStoreReportPreviewDialog>

      <Dialog open={viewOrderDialog.open} onClose={closeOrderDetails} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2, maxHeight: "90vh" } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
            <Box>
              <Typography variant="overline" color="text.secondary">PROMOSTORE ORDER</Typography>
              <Typography variant="h6" fontWeight={700}>#{getOrderNumber(viewOrderDialog.order || {})}</Typography>
            </Box>
            <Chip label={getOrderStatus(viewOrderDialog.order || {})} color={String(getOrderStatusRaw(viewOrderDialog.order || {})).toLowerCase().includes("complet") ? "success" : "default"} size="small" />
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: "background.default" }}>
          <Stack spacing={2.5} sx={{ py: 1 }}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary">CUSTOMER</Typography>
                  <Typography fontWeight={700}>{getOrderCustomerName(viewOrderDialog.order || {})}</Typography>
                  <Typography variant="body2" color="text.secondary">Placed {getOrderDate(viewOrderDialog.order || {})}</Typography>
                  {viewOrderDialog.order?.rawPayload?.billing?.email && <Typography variant="body2">{viewOrderDialog.order.rawPayload.billing.email}</Typography>}
                  {viewOrderDialog.order?.rawPayload?.billing?.phone && <Typography variant="body2">{viewOrderDialog.order.rawPayload.billing.phone}</Typography>}
                </Box>
                <Box sx={{ textAlign: { xs: "left", sm: "right" } }}>
                  <Typography variant="caption" color="text.secondary">ORDER TOTAL</Typography>
                  <Typography variant="h5" fontWeight={700}>{getOrderTotalDisplay(viewOrderDialog.order || {})}</Typography>
                </Box>
              </Stack>
            </Paper>
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle1" fontWeight={700}>Ordered products</Typography>
                <Chip size="small" variant="outlined" label={`${getOrderItems(viewOrderDialog.order || {}).length} line items`} />
              </Stack>
              <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
                {getOrderItems(viewOrderDialog.order || {}).length ? (
                  <Stack divider={<Box sx={{ borderBottom: 1, borderColor: "divider" }} />}>
                    {getOrderItems(viewOrderDialog.order || {}).map((item, index) => (
                      <Box key={`${item.id ?? item.productId ?? "item"}-${index}`} sx={{ p: 2 }}>
                        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={2}>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="subtitle2" fontWeight={700} sx={{ overflowWrap: "anywhere" }}>{item.productName}</Typography>
                            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                              {item.sku && <Chip label={`SKU: ${item.sku}`} size="small" variant="outlined" />}
                              {item.variationId && <Chip label={`Variation ID: ${item.variationId}`} size="small" variant="outlined" />}
                              {item.variation && <Chip label={item.variation} size="small" variant="outlined" sx={{ height: "auto", "& .MuiChip-label": { whiteSpace: "normal", py: 0.5 } }} />}
                            </Stack>
                            {item.decoration && <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Decoration: {item.decoration}</Typography>}
                          </Box>
                          <Box sx={{ minWidth: { sm: 160 }, textAlign: { xs: "left", sm: "right" } }}>
                            <Typography variant="body2" color="text.secondary">Qty {item.quantity} × {formatOrderItemMoney(item.unitPrice, viewOrderDialog.order || {})}</Typography>
                            <Typography fontWeight={700}>{formatOrderItemMoney(item.lineTotal, viewOrderDialog.order || {})}</Typography>
                          </Box>
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                ) : <Typography color="text.secondary" sx={{ p: 2 }}>No order items were returned.</Typography>}
              </Paper>
            </Box>
            {getPromoStoreCheckoutFields(viewOrderDialog.order || {}).length > 0 && (
              <Box>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Checkout details</Typography>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Stack spacing={1.25}>
                    {getPromoStoreCheckoutFields(viewOrderDialog.order || {}).map(({ key, label, value }) => (
                      <Stack key={key} direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={0.5}>
                        <Typography variant="body2" color="text.secondary">{label}</Typography>
                        <Typography variant="body2" fontWeight={600} sx={{ overflowWrap: "anywhere" }}>{value}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Paper>
              </Box>
            )}
            <Box>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Shipping & billing</Typography>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={3}>
                  {["shipping", "billing"].map((type) => {
                    const raw = viewOrderDialog.order?.rawPayload || {};
                    const address = (type === "shipping" ? viewOrderDialog.order?.shippingAddress : viewOrderDialog.order?.billingAddress) || raw[type] || {};
                    const lines = [
                      [address.first_name, address.last_name].filter(Boolean).join(" ") || address.name,
                      address.company,
                      address.address_1 || address.address1,
                      address.address_2 || address.address2,
                      [address.city, address.state, address.postcode || address.zip].filter(Boolean).join(", "),
                      address.country,
                    ].filter(Boolean);
                    return <Box key={type} sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase" }}>{type} address</Typography>
                      {lines.length ? lines.map((line, i) => <Typography key={i} variant="body2" sx={{ overflowWrap: "anywhere" }}>{line}</Typography>) : <Typography variant="body2" color="text.secondary">Not provided</Typography>}
                    </Box>;
                  })}
                </Stack>
              </Paper>
            </Box>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Order totals</Typography>
              {(() => {
                const order = viewOrderDialog.order || {};
                const raw = order.rawPayload || order;
                const currency = raw.currency || order.currency || "CAD";
                const lineSubtotal = getOrderItems(order).reduce((sum, item) => sum + (Number(item.raw?.subtotal ?? item.lineTotal) || 0), 0);
                const rows = [["Subtotal", raw.subtotal ?? lineSubtotal], ["Shipping", raw.shipping_total ?? 0], ["Tax", raw.total_tax ?? 0]];
                return <Stack spacing={0.8}>
                  {rows.map(([label, value]) => <Stack key={label} direction="row" justifyContent="space-between"><Typography variant="body2" color="text.secondary">{label}</Typography><Typography variant="body2">{formatCurrencyAmount(value, currency)}</Typography></Stack>)}
                  <Box sx={{ borderTop: 1, borderColor: "divider", pt: 1 }}><Stack direction="row" justifyContent="space-between"><Typography fontWeight={700}>Total</Typography><Typography fontWeight={700}>{getOrderTotalDisplay(order)}</Typography></Stack></Box>
                  {(raw.shipping_lines || []).map((line, i) => <Typography key={line.id || i} variant="caption" color="text.secondary">Shipping method: {line.method_title || line.method_id || "Not specified"}</Typography>)}
                </Stack>;
              })()}
            </Paper>
            {canViewRawOrderData && <Box component="details" sx={{ "& summary": { cursor: "pointer" } }}>
              <Typography component="summary" variant="body2" color="text.secondary" fontWeight={600}>Technical order data (read-only)</Typography>
              <Stack spacing={2} sx={{ mt: 2 }}>
                <TextField label="Stored order items" multiline minRows={4} maxRows={12} value={JSON.stringify(getOrderItems(viewOrderDialog.order || {}).map((item) => item.raw), null, 2)} fullWidth InputProps={{ readOnly: true }} />
                <TextField label="Original order payload" multiline minRows={4} maxRows={12} value={JSON.stringify(viewOrderDialog.order?.rawPayload || viewOrderDialog.order || {}, null, 2)} fullWidth InputProps={{ readOnly: true }} />
              </Stack>
            </Box>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={closeOrderDetails} variant="contained">Close</Button>
        </DialogActions>
      </Dialog>


    </Box>
  );
};

const thSx = {
  textAlign: "left",
  px: 2,
  py: 1.5,
  fontSize: 12,
  fontWeight: 600,
  color: "text.secondary",
  textTransform: "uppercase",
  borderBottom: "1px solid #e0e0e0",
};

const tdSx = {
  px: 2,
  py: 1.25,
  borderBottom: "1px solid #f0f0f0",
  fontSize: 14,
  whiteSpace: "nowrap",
};

const tdStrongSx = { ...tdSx, fontWeight: 600 };

const tdEllipsisSx = {
  ...tdSx,
  maxWidth: 260,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

InstanceWebStoreOrders.propTypes = {
  orders: PropTypes.arrayOf(PropTypes.object),
  storeType: PropTypes.string,
  storeStatus: PropTypes.string,
  instanceUniqueId: PropTypes.string,
  storeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  storeName: PropTypes.string,
  isActive: PropTypes.bool,
};

export default InstanceWebStoreOrders;
